import React from 'react';
import { ChevronLeft, ChevronRight, Receipt } from 'lucide-react';
import { Card, EmptyState, IconButton, SectionHeader, Skeleton, StatGrid } from '../../../components/ui';
import type { UsageBucket, UsageSummary } from '../../../types/aiOps';
import {
  currentMonth,
  featureLabel,
  formatCount,
  formatMoney,
  formatShare,
  hasCap,
  isFutureMonth,
  monthLabel,
  shiftMonth,
} from '../format';
import { DailySpend } from './DailySpend';
import { MetaLine, Notice, ShareBar } from './ui';

interface SpendSectionProps {
  usage: UsageSummary | null;
  month: string;
  loading: boolean;
  error: string | null;
  onMonthChange: (month: string) => void;
  className?: string;
}

/** What the AI actually cost this month, and how much of it bought nothing. */
export const SpendSection: React.FC<SpendSectionProps> = ({
  usage,
  month,
  loading,
  error,
  onMonthChange,
  className = '',
}) => {
  const atLatestMonth = month >= currentMonth();
  const future = isFutureMonth(month);
  const spent = usage?.totalCostCents ?? 0;
  const calls = usage?.totalCalls ?? 0;
  const failures = usage?.failures ?? 0;
  const budget = usage?.budget;
  const perCall = calls > 0 ? spent / calls : 0;

  // Only the month being lived in can be stopping work right now
  const budgetNotice = (() => {
    if (!budget || !hasCap(budget) || month !== currentMonth()) return null;
    const share = spent / budget.monthlyCapCents;
    if (share >= budget.pauseScheduledAt) {
      return {
        tone: 'error' as const,
        text: `Scheduled work is stopped — ${monthLabel(month)} is past ${formatShare(budget.pauseScheduledAt)} of the ${formatMoney(
          budget.monthlyCapCents,
        )} ceiling. People can still run things by hand.`,
      };
    }
    if (share >= budget.warnAt) {
      return {
        tone: 'quiet' as const,
        text: `Past the ${formatShare(budget.warnAt)} warning. Scheduled work stops at ${formatShare(
          budget.pauseScheduledAt,
        )} — ${formatMoney(budget.monthlyCapCents * budget.pauseScheduledAt)}.`,
      };
    }
    return null;
  })();

  return (
    <section className={className}>
      <SectionHeader
        title="What it costs"
        aside={
          <div className="flex items-center gap-1">
            <IconButton label="Previous month" size="sm" onClick={() => onMonthChange(shiftMonth(month, -1))}>
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
            <span className="min-w-[104px] text-center text-[13px] text-ink">{monthLabel(month)}</span>
            <IconButton
              label="Next month"
              size="sm"
              disabled={atLatestMonth}
              onClick={() => onMonthChange(shiftMonth(month, 1))}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </div>
        }
      />

      {error && <Notice className="mb-3">{error}</Notice>}

      {loading && !usage ? (
        <Card className="p-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-4 h-24 w-full" />
        </Card>
      ) : !usage || calls === 0 ? (
        <Card>
          <EmptyState
            icon={Receipt}
            title={future ? `${monthLabel(month)} has not happened yet` : `Nothing spent in ${monthLabel(month)}`}
          />
        </Card>
      ) : (
        <>
          {budgetNotice && (
            <Notice className="mb-3" tone={budgetNotice.tone}>
              {budgetNotice.text}
            </Notice>
          )}

          <StatGrid
            items={[
              {
                label: 'Spent',
                value: formatMoney(spent),
                hint: hasCap(budget)
                  ? `${formatShare((usage.spentPercent ?? 0) / 100)} of the ${formatMoney(budget!.monthlyCapCents)} ceiling`
                  : 'No ceiling set',
              },
              { label: 'Calls', value: formatCount(calls), hint: `${formatMoney(perCall)} each on average` },
              {
                label: 'Failed calls',
                value: formatCount(failures),
                hint: failures > 0 ? 'Billed, returned nothing' : 'Everything answered',
              },
              {
                label: 'Tokens out',
                value: formatCount(usage.daily.reduce((total, day) => total + day.outputTokens, 0)),
                hint: 'Across every provider',
              },
            ]}
          />

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BreakdownCard
              title="By feature"
              total={spent}
              rows={usage.byFeature.map((bucket) => ({ key: String(bucket.feature), label: featureLabel(bucket.feature), bucket }))}
            />
            <BreakdownCard
              title="By provider"
              total={spent}
              rows={usage.byProvider.map((bucket) => ({ key: bucket.provider, label: bucket.provider, bucket }))}
            />
          </div>

          <Card padded className="mt-4">
            <h3 className="mb-3 text-[13px] text-ink">By day</h3>
            <DailySpend month={month} daily={usage.daily} />
          </Card>
        </>
      )}
    </section>
  );
};

interface BreakdownRow {
  key: string;
  label: string;
  bucket: UsageBucket;
}

const BreakdownCard: React.FC<{ title: string; rows: BreakdownRow[]; total: number }> = ({ title, rows, total }) => {
  const ordered = [...rows].sort((a, b) => b.bucket.costCents - a.bucket.costCents);

  return (
    <div>
      <h3 className="mb-2 text-[13px] text-neutral-500">{title}</h3>
      <Card divided>
        {ordered.length === 0 ? (
          <p className="px-4 py-6 text-center text-[13px] text-neutral-400">Nothing yet</p>
        ) : (
          ordered.map((row) => (
            <div key={row.key} className="px-4 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-[13px] text-ink">{row.label}</span>
                <span className="flex-shrink-0 text-[13px] tabular-nums text-ink">{formatMoney(row.bucket.costCents)}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-3">
                <MetaLine
                  className="min-w-0 flex-1"
                  parts={[
                    `${formatCount(row.bucket.calls)} ${row.bucket.calls === 1 ? 'call' : 'calls'}`,
                    row.bucket.failures > 0 && (
                      <span className="text-primary-700">
                        {formatCount(row.bucket.failures)} failed
                      </span>
                    ),
                  ]}
                />
                <span className="w-16 flex-shrink-0">
                  <ShareBar value={total > 0 ? row.bucket.costCents / total : 0} />
                </span>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
};
